import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Typography,
  Button,
  IconButton,
  TextField,
  Box,
  Tooltip,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
  Fade,
  InputAdornment,
  Collapse,
  CircularProgress,
  Chip,
  useScrollTrigger,
} from '@mui/material';
// SettingsBrightnessIcon removed (unused)

import ActionIcon from "../../images/out.png";
import SettingIcon from "../../images/icon_setting_dashboard.png";
import DatasourceIcon from "../../images/in.png";
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
// RenameIcon removed (unused)
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ActionsDataflowList from '../Actions/ActionsDataflowList';
import SettingDatasources from '../SettingDatasources';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import ShareIcon from '@mui/icons-material/Share';
import AutoAwesomeMosaicIcon from '@mui/icons-material/AutoAwesomeMosaic';
import '../../i18n';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../Utils/SnackBarContext';
import CloseButton from '../Common/CloseButton';
// StorageIcon removed (unused)

// Memoized style constants
const STYLES = {
  paper: {
    borderRadius: 2,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    background: '#fff',
    padding: { xs: 1.5, md: 2 },
    boxSizing: 'border-box',
    width: '100%',
    overflow: 'hidden',
    mb: 1,
    border: '1px solid rgba(0,0,0,0.06)',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
    transition: 'box-shadow 0.2s ease',
    zIndex: 10, // Keep header above most content to avoid overlays
    // minHeight removed here to allow responsive / collapsed sizing
    '&:hover': {
      boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)'
    }
  },
  stickyPaper: {
    position: 'sticky',
    top: 0,
    zIndex: 1100,
    borderRadius: 0,
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.12)',
    padding: { xs: 1, md: 1.5 },
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    minHeight: '80px', // Reserve minimum space for sticky mode
  },
  dialogTitle: {
    borderBottom: '1px solid rgba(0,0,0,0.1)',
    background: 'linear-gradient(to right, #5757b0, #8080d7)',
    color: 'white',
    fontWeight: 600 
  },
  expandButton: {
    backgroundColor: '#fff',
    border: '1px solid rgba(87, 87, 176, 0.4)',
    transition: 'all 0.2s ease',
    position: 'absolute',
    bottom: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 9999,
    boxShadow: '0 6px 14px rgba(87, 87, 176, 0.25)',
    pointerEvents: 'auto',
    width: '36px',
    height: '36px',
    color: '#5757b0',
    '&:hover': {
      transform: 'translateX(-50%) scale(1.03)',
    },
    '& svg': {
      fontSize: '20px',
    }
  }
};

const HEADER_BADGE_SX = {
  borderRadius: '999px',
  height: 30,
  px: 0.5,
  border: '1px solid rgba(87, 87, 176, 0.18)',
  backgroundColor: 'rgba(87, 87, 176, 0.06)',
  color: '#373f8c',
  fontWeight: 600,
  backdropFilter: 'blur(8px)',
  transition: 'all 0.18s ease',
  '& .MuiChip-icon': {
    color: '#5757b0',
    ml: 0.75,
    mr: -0.25
  },
  '& .MuiChip-label': {
    px: 1,
    fontSize: '0.78rem'
  },
  '&:hover': {
    backgroundColor: 'rgba(87, 87, 176, 0.1)',
    borderColor: 'rgba(87, 87, 176, 0.3)'
  }
};

// Extracted control button component for reusability
// Renders an IconButton with a visible caption beneath it (keeps tooltip and accessibility)
const ControlButton = React.memo(({ title, label, onClick, icon, isSticky, expanded = true, active = false }) => (
  <Tooltip title={title} arrow placement="bottom">
    <Box
      role="button"
      tabIndex={0}
      onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick && onClick(); }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: expanded ? 0.45 : 0.25,
        width: isSticky ? (expanded ? 40 : 34) : (expanded ? 64 : 48),
        cursor: 'pointer',
        userSelect: 'none'
      }}
    >
      <IconButton
        onClick={onClick}
        size="small"
        aria-pressed={active}
        aria-label={title}
        sx={{
          borderRadius: 1,
          border: active ? '1px solid rgba(87,87,176,0.8)' : '1px solid rgba(87, 87, 176, 0.3)',
          width: isSticky ? (expanded ? 34 : 30) : (expanded ? 40 : 34),
          height: isSticky ? (expanded ? 34 : 30) : (expanded ? 40 : 34),
          backgroundColor: active ? 'rgba(87,87,176,0.15)' : '#fff',
          color: active ? '#373f8c' : 'inherit',
          transition: 'all 0.12s ease',
          '&:hover': {
            backgroundColor: active ? 'rgba(87,87,176,0.25)' : 'rgba(87, 87, 176, 0.08)'
          }
        }}
      >
        {icon}
      </IconButton>

      <Typography
        variant="caption"
        sx={{
          mt: 0.25,
          fontSize: isSticky ? (expanded ? '0.65rem' : '0.6rem') : (expanded ? '0.75rem' : '0.65rem'),
          color: active ? '#373f8c' : 'rgba(34,37,52,0.64)',
          textAlign: 'center',
          lineHeight: 1,
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {label || title}
      </Typography>
    </Box>
  </Tooltip>
));

// Helper function for responsive icon styling (now considers `expanded` state)
const getIconStyle = (isSticky, expanded = true, useTheme = false) => {
  // Prioritize sticky -> compact; when collapsed (expanded === false) use a smaller icon
  const size = isSticky ? '20px' : (expanded ? '24px' : '18px');

  return {
    width: size,
    height: size,
    display: 'block', // Prevent inline spacing issues
    objectFit: 'contain', // Maintain aspect ratio
    transition: 'all 0.16s ease'
  };
};

const DashboardHeader = React.memo(({
  title,
  setTitle,
  description,
  setDescription,
  folder,
  openFolder,
  sendMessage,
  caseid,
  tags,
  templateInfo = null,
  onDatasourceClick,
  visibleSection,
  isReadOnly,
  canManageProject = false,
  autoOpenDatasource,
  autoOpenDatasourceId,
  onDatasourceOpened,
  autoOpenActions,
  autoOpenExportId,
  onActionsOpened,
  onSettingsOpen,
  assignedCollections = [],
  hasMoreAssignedCollections = false,
  hasAdditionalCollections = false,
  collectionsLoading = false,
  collectionActionLoading = false,
  onCollectionChipClick,
  onMoreCollections,
  settingsDialogOpen,
  isAdmin = false,
  onShareOpen
}) => {
  
  const { openSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [showRename, setShowRename] = useState(false);  const [expanded, setExpanded] = useState(true);
  const [showDatasource, setShowDatasource] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  
  // Auto-open datasource modal after OAuth redirect
  useEffect(() => {
    if (autoOpenDatasource && !showDatasource) {
      setShowDatasource(true);
      if (onDatasourceOpened) {
        onDatasourceOpened();
      }
    }
  }, [autoOpenDatasource, showDatasource, onDatasourceOpened]);

  // Auto-open actions modal after OAuth redirect
  useEffect(() => {
    if (autoOpenActions && !showRename) {
      setShowRename(true);
      if (onActionsOpened) {
        onActionsOpened();
      }
    }
  }, [autoOpenActions, showRename, onActionsOpened]);
  
  // Use two different thresholds to create hysteresis and prevent flickering
  const enterStickyTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100, // Threshold to enter sticky mode
  });
  
  const exitStickyTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 70, // Lower threshold to exit sticky mode (creates hysteresis)
  });

  // Update sticky state based on scrolling with hysteresis to prevent flickering
  useEffect(() => {
    if (enterStickyTrigger && !isSticky) {
      // Entering sticky mode
      setExpanded(false); // Auto-collapse when scrolling
      setIsSticky(true);
    } else if (!exitStickyTrigger && isSticky) {
      // Exiting sticky mode - only when scrolling well above the threshold
      setIsSticky(false);
    }
  }, [enterStickyTrigger, exitStickyTrigger, isSticky]);

  // Local state for title and description
  const [localTitle, setLocalTitle] = useState(title);
  const [localDescription, setLocalDescription] = useState(description);

  const handleTitleChange = useCallback((e) => setLocalTitle(e.target.value), []);
  const handleDescriptionChange = useCallback((e) => setLocalDescription(e.target.value), []);

  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    setTitle(localTitle);
    sendMessage({ command: 'changetitle', caseid, title: localTitle });
    openSnackbar("Summary updated successfully", 3000);
  }, [sendMessage, caseid, localTitle, setTitle, openSnackbar]);

  const handleDescriptionBlur = useCallback(() => {
    setIsEditingDescription(false);
    setDescription(localDescription);
    sendMessage({ command: 'changedescription', caseid, description: localDescription });
    openSnackbar("Summary updated successfully", 3000);
  }, [sendMessage, caseid, localDescription, setDescription, openSnackbar]);

  const handleTitleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleTitleBlur();
    } else if (e.key === 'Escape') {
      setLocalTitle(title); // Reset to original title
      setIsEditingTitle(false);
    }
  }, [handleTitleBlur, title]);

  const handleDescriptionKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleDescriptionBlur();
    } else if (e.key === 'Escape') {
      setLocalDescription(description); // Reset to original description
      setIsEditingDescription(false);
    }
  }, [handleDescriptionBlur, description]);

  // Memoized handlers
  const handleEditTitle = useCallback(() => {
    if (canManageProject) {
      setIsEditingTitle(true);
    }
  }, [canManageProject]);

  const handleEditDescription = useCallback(() => {
    if (canManageProject) {
      setIsEditingDescription(true);
    }
  }, [canManageProject]);

  const handleToggleRename = useCallback((show) => () => {
    setShowRename(show);
  }, []);

  const handleToggleDatasource = useCallback((show) => () => {
    setShowDatasource(show);
  }, []);

  const toggleExpanded = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  // Sync local state with props efficiently - only when not editing
  useEffect(() => {
    if (!isEditingTitle && title !== localTitle) {
      setLocalTitle(title);
    }
  }, [title, localTitle, isEditingTitle]);

  useEffect(() => {
    if (!isEditingDescription && description !== localDescription) {
      setLocalDescription(description);
    }
  }, [description, localDescription, isEditingDescription]);

  const isTitlePlaceholder = !(localTitle && localTitle.trim());
  const isDescriptionPlaceholder = !(localDescription && localDescription.trim());

  // Memoized complex UI elements
  const titleSection = useMemo(() => (
    <Typography 
      variant="h5" 
      sx={{ 
        width: '100%',
        textAlign: 'center',
        fontWeight: 600,
        color: isTitlePlaceholder ? '#94a3b8' : '#1f2432',
        position: 'relative',
        transition: 'color 0.2s ease',
        cursor: isEditingTitle ? 'default' : (canManageProject ? 'pointer' : 'default'),
        /* Reduce title size when header is collapsed to avoid vertical clipping */
        fontSize: {
          xs: isSticky ? '1.2rem' : (expanded ? '1.4rem' : '1.05rem'),
          sm: isSticky ? '1.3rem' : (expanded ? '1.7rem' : '1.2rem'),
          md: isSticky ? '1.4rem' : (expanded ? '1.9rem' : '1.25rem')
        },
        lineHeight: 1.2,
        wordBreak: 'break-word',
        '&:hover': !isEditingTitle && canManageProject ? {
          color: '#5757b0',
        } : {}
      }}
    >
      {isEditingTitle ? (
        <TextField
          value={localTitle}
          onChange={handleTitleChange}
          onBlur={handleTitleBlur}
          onKeyDown={handleTitleKeyDown}
          autoFocus
          fullWidth
          variant="standard"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <EditIcon fontSize="small" color="primary" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: isSticky ? '1.3rem' : '1.8rem',
              fontWeight: 600,
              textAlign: 'center'
            }
          }}
        />
      ) : (
        <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
          <Box 
            onClick={handleEditTitle} 
            sx={{ 
              display: 'inline-flex', 
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              width: { xs: 'auto', md: '100%' },
              cursor: canManageProject ? 'pointer' : 'default',
              ...(canManageProject ? {
                '&:hover .editIcon': {
                  opacity: 1,
                  transform: 'translateX(0)'
                }
              } : {})
            }}
          >
            {isTitlePlaceholder ? t('dashboard.add_title', 'Add title') : localTitle}
          </Box>

          {/* Desktop inline edit icon — hidden on xs so it doesn't affect centering */}
          {canManageProject && (
            <Box sx={{ display: { xs: 'none', sm: 'inline-flex' }, ml: 1, position: 'absolute', right: { md: 'calc(50% - 8px)', lg: 'calc(50% - 8px)' } }}>
              <EditIcon
                className="editIcon"
                fontSize="small"
                sx={{
                  ml: 1,
                  opacity: 0,
                  transform: 'translateX(-10px)',
                  transition: 'all 0.2s ease',
                  color: '#5757b0',
                  fontSize: isSticky ? '0.8rem' : '1rem'
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </Typography>
  ), [isEditingTitle, localTitle, handleTitleChange, handleTitleBlur, handleTitleKeyDown, handleEditTitle, isSticky, expanded, isTitlePlaceholder, t, canManageProject]);

const descriptionSection = useMemo(() => (
  <Box sx={{ 
    my: 2, 
      width: '100%', 
      textAlign: 'center',
      minHeight: '38px', // Reserve space to prevent layout shift (smaller)
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {isEditingDescription ? (
        <TextField
          value={localDescription}
          onChange={handleDescriptionChange}
          onBlur={handleDescriptionBlur}
          onKeyDown={handleDescriptionKeyDown}
          multiline
          fullWidth
          variant="outlined"
          label={t('dashboard.description_label')}
          rows={3}
          autoFocus
          InputProps={{
            sx: {
              textAlign: 'center'
            }
          }}
        />
      ) : (
        <Typography 
          variant="body1" 
          onClick={handleEditDescription}
          sx={{
            cursor: canManageProject ? 'pointer' : 'default',
            color: isDescriptionPlaceholder ? '#94a3b8' : '#4a5568',
            borderRadius: 1,
            padding: 0,
            textAlign: 'center',
            minHeight: '24px', // Reserve minimum height for text
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            whiteSpace: 'pre-line', // Preserve line breaks in description
            wordBreak: 'break-word',
            ...(canManageProject ? {
              '&:hover': {
                color: '#2c3e50'
              }
            } : {})
          }}
        >
          {isDescriptionPlaceholder ? t('dashboard.add_description', 'Add description') : localDescription}
        </Typography>
      )}
  </Box>
), [isEditingDescription, localDescription, handleDescriptionChange, handleDescriptionBlur, handleDescriptionKeyDown, handleEditDescription, isDescriptionPlaceholder, t, canManageProject]);

  const collectionQuickAddPanel = useMemo(() => {
    if (!canManageProject || !caseid || !onMoreCollections) {
      return null;
    }

    const hasChips = assignedCollections.length > 0;
    const showMoreButton = Boolean(onMoreCollections && (hasMoreAssignedCollections || hasAdditionalCollections));

    const hasTemplate = Boolean(templateInfo && templateInfo.id);

    if (!hasChips && !hasTemplate && !collectionsLoading && !showMoreButton && !collectionActionLoading) {
      return null;
    }

    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
          mb: 1,
          pt: 0.5
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '28px'
          }}
        >
          {collectionsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('collection.loading_collections', 'Loading collections...')}
              </Typography>
            </Box>
          ) : !hasChips && !hasTemplate ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('collection.not_in_any_collection', 'No collection')}
            </Typography>
          ) : null}
          {!collectionsLoading && hasTemplate ? (
            <Tooltip
              title={t('dashboard.template_origin_tooltip', 'Template used to create this project')}
              arrow
            >
              <Chip
                icon={<AutoAwesomeMosaicIcon sx={{ fontSize: 16 }} />}
                label={`${templateInfo?.title || t('dashboard.template_fallback', 'Template')} (#${templateInfo?.id})`}
                size="small"
                variant="outlined"
                sx={HEADER_BADGE_SX}
              />
            </Tooltip>
          ) : null}
          {!collectionsLoading && hasChips ? (
            assignedCollections.map((collection) => (
              <Tooltip
                key={`quick-collection-${collection?.id}`}
                title={t('collection.add_to_collection_tooltip', 'Add this case to a collection')}
                arrow
              >
                <Chip
                  icon={<CollectionsBookmarkIcon sx={{ fontSize: 16 }} />}
                  label={collection?.title || t('sidebar.untitled_collection', 'Untitled Collection')}
                  onClick={() => onCollectionChipClick && onCollectionChipClick(collection?.id)}
                  clickable={!collectionActionLoading}
                  disabled={collectionActionLoading}
                  size="small"
                  variant="outlined"
                  sx={HEADER_BADGE_SX}
                />
              </Tooltip>
            ))
          ) : null}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75
          }}
        >
          {collectionActionLoading && (
            <CircularProgress size={16} />
          )}
          {showMoreButton && (
            <Tooltip title={t('dashboard.collections_more', 'More collections')} arrow>
              <IconButton
                onClick={onMoreCollections}
                disabled={collectionsLoading || collectionActionLoading}
                size="small"
                sx={{
                  borderRadius: '999px',
                  border: '1px solid rgba(87, 87, 176, 0.35)',
                  color: '#373f8c',
                  '&:hover': {
                    borderColor: 'rgba(87, 87, 176, 0.6)',
                    backgroundColor: 'rgba(87, 87, 176, 0.08)'
                  }
                }}
              >
                <CollectionsBookmarkIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  }, [
    canManageProject,
    caseid,
    collectionsLoading,
    collectionActionLoading,
    assignedCollections,
    templateInfo,
    hasMoreAssignedCollections,
    hasAdditionalCollections,
    onCollectionChipClick,
    onMoreCollections,
    t
  ]);

  const controlButtons = useMemo(() => {
    const buttons = [];

    if (canManageProject) {
      buttons.push(
        <ControlButton
          key="datasource"
          title={t('dataconnectors.title') || 'Data Source'}
          label={t('dataconnectors.title') || 'Data Source'}
          onClick={handleToggleDatasource(true)}
          isSticky={isSticky}
          expanded={expanded}
          icon={<img src={DatasourceIcon} alt="Datasource Icon" style={getIconStyle(isSticky, expanded)} />}
        />,
        <ControlButton
          key="actions"
          title={t('settings.title_dataflow')}
          label={t('settings.title_dataflow')}
          onClick={handleToggleRename(true)}
          isSticky={isSticky}
          expanded={expanded}
          icon={<img src={ActionIcon} alt="Action Icon" style={getIconStyle(isSticky, expanded)} />}
        />,
        <ControlButton
          key="settings"
          title={t('dashboard.settings')}
          label={t('dashboard.settings')}
          onClick={onSettingsOpen}
          isSticky={isSticky}
          expanded={expanded}
          active={visibleSection === "settings" || settingsDialogOpen}
          icon={<img src={SettingIcon} alt="Settings Icon" style={getIconStyle(isSticky, expanded)} />}
        />
      );
    }

    if (isAdmin && onShareOpen) {
      buttons.push(
        <ControlButton
          key="share"
          title={t('project_share.title', 'Share Project')}
          label={t('project_share.share_label', 'Share')}
          onClick={onShareOpen}
          isSticky={isSticky}
          expanded={expanded}
          icon={<ShareIcon sx={{ fontSize: isSticky ? (expanded ? 20 : 18) : (expanded ? 24 : 18), color: '#5757b0' }} />}
        />
      );
    }

    if (buttons.length === 0) {
      return null;
    }

    return <Box sx={{ display: 'flex', gap: expanded ? 1 : 0.5, flexWrap: 'wrap' }}>{buttons}</Box>;
  }, [canManageProject, isAdmin, onShareOpen, isSticky, expanded, t, handleToggleDatasource, handleToggleRename, visibleSection, onSettingsOpen, settingsDialogOpen]);

  const datasourceDialog = useMemo(() => showDatasource && (
    <Dialog
      open={showDatasource}
      onClose={handleToggleDatasource(false)}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          width: '90%',
          maxWidth: '900px',
        }
      }}
    >
      <DialogTitle sx={{
        ...STYLES.dialogTitle,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pr: 1
      }}>
        {t('dataconnectors.title') || 'Datasource'}
        <CloseButton
          ariaLabel="close"
          onClick={handleToggleDatasource(false)}
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            '&:hover': {
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        />
      </DialogTitle>
      <DialogContent sx={{ mt: { xs: 1, sm: 2 }, p: { xs: 1, sm: 3 } }}>
          <SettingDatasources 
            caseid={caseid} 
            hideHeader={true} 
            onClose={handleToggleDatasource(false)} 
            autoOpenConnector={autoOpenDatasource}
            autoOpenDatasourceId={autoOpenDatasourceId}
            onConnectorOpened={onDatasourceOpened}
          />
      </DialogContent>
    </Dialog>
  ), [showDatasource, handleToggleDatasource, t, caseid, autoOpenDatasource, autoOpenDatasourceId, onDatasourceOpened]);

  // Lazy load dialog only when needed
  const createFileDialog = useMemo(() => showRename && (
    <Dialog 
      open={showRename} 
      onClose={handleToggleRename(false)} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          width: '90%',
          maxWidth: '900px', // Increase max width for larger dialog
        }
      }}
    >      <DialogTitle sx={{
        ...STYLES.dialogTitle,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pr: 1
      }}>
        {t('dashboard.rename_files')}
        <CloseButton
          ariaLabel="close"
          onClick={handleToggleRename(false)}
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            '&:hover': {
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        />
      </DialogTitle>
      <DialogContent sx={{ mt: 1, p: { xs: 1, sm: 3 } }}>        
        <ActionsDataflowList 
          caseid={caseid} 
          onClose={handleToggleRename(false)}
          autoOpenExportId={autoOpenExportId}
          onExportOpened={onActionsOpened}
        />
      </DialogContent>
    </Dialog>
  ), [showRename, handleToggleRename, caseid, t, autoOpenExportId, onActionsOpened]);

  // Folder section component
  const folderSection = useMemo(() => (
    folder && folder.trim() !== "" ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <Tooltip title={t('dashboard.open_folder')} arrow>
          <IconButton 
            onClick={openFolder}
            size="small"
            sx={{
              border: '1px solid rgba(87, 87, 176, 0.3)',
              color: '#5757b0',
              '&:hover': {
                backgroundColor: 'rgba(87, 87, 176, 0.08)'
              }
            }}
          >
            <FolderOpenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography 
          variant="caption" 
          color="textSecondary"
          sx={{
            fontFamily: 'monospace',
            maxWidth: { xs: '100%', md: '320px' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {folder}
        </Typography>
      </Box>
    ) : null
  ), [folder, openFolder, t]);

  const expandButtonLabel = expanded 
    ? t('dashboard.collapse_header') || 'Collapse header' 
    : t('dashboard.expand_header') || 'Expand header';

  return (
    <Fade in={true} timeout={800}>
      <Paper elevation={isSticky ? 4 : 3} sx={{
          ...STYLES.paper,
          ...(isSticky ? STYLES.stickyPaper : {}),
          position: isSticky ? 'sticky' : 'relative',
        py: expanded ? 2 : 1,
        pb: expanded ? 2 : 1,
          /* Increase collapsed minHeight to avoid clipping of title when header is closed */
          minHeight: isSticky ? STYLES.stickyPaper.minHeight : (expanded ? '96px' : '64px'),
          overflow: 'visible',
          contain: 'layout', // Improve layout performance
          willChange: isSticky ? 'position, transform' : 'auto', // Optimize sticky transitions
        }}>
        <Container maxWidth={isSticky ? "xl" : "lg"} sx={{ 
          px: isSticky ? { xs: 1, sm: 2, md: 3 } : { xs: 1, sm: 2, md: 3 }, // Reduced responsive padding to reduce header vertical footprint
          overflow: 'visible', // Allow buttons to be visible
          position: 'relative', // Establish positioning context
          width: '100%'
        }}>
          <Box sx={{ position: 'relative', maxWidth: '100%', overflow: 'visible' }}>
            {/* Control buttons positioned absolutely on the right */}
            <Box sx={{ 
              position: 'absolute',
              /* lift the controls slightly to prevent label overflow; animate for smoothness */
              top: isSticky ? 8 : (expanded ? -6 : -10),
              right: 0,
              zIndex: 10,
              display: { xs: 'none', md: 'flex' }, // Hide on mobile, show on desktop
              alignItems: 'flex-start',
              minHeight: '40px', // Reserve space for buttons
              minWidth: '220px', // Reserve space for button row
              transition: 'top 180ms ease, transform 160ms ease',
            }}>
              {controlButtons}
            </Box>

            {/* Title + folder + mobile buttons share one centered container on small screens */}
            <Box sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mx: 'auto',
              /* Constrain visual width on xs so title and buttons align */
              maxWidth: { xs: '640px', md: '100%' },
              mb: expanded && !isSticky ? 1 : 0.5,
              pb: { xs: expanded ? 2.5 : 1, md: 0 },
              px: { xs: 1.25, md: 0 }
            }}>
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                {titleSection}
              </Box>

              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                {folderSection}
              </Box>

              {/* Mobile control buttons — centered and constrained to the same maxWidth */}
              <Box sx={{
                display: { xs: 'flex', md: 'none' },
                justifyContent: 'center',
                mt: expanded ? 1.5 : 0.75,
                mb: expanded ? 1 : 0.5,
                minHeight: '40px',
                transform: expanded ? 'translateY(0)' : 'translateY(-6px)',
                transition: 'transform 160ms ease, margin 160ms ease',
                width: '100%'
              }}>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                  {controlButtons}
                </Box>
              </Box>
            </Box>

            <Collapse in={expanded} timeout="auto">
              {collectionQuickAddPanel}
              {descriptionSection}
            </Collapse>
          </Box>
        </Container>
        
        {/* Toggle expand/collapse button - hide when sticky */}
        {!isSticky && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -6,
              left: 0,
              width: '100%',
              height: 20,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
                zIndex: 2000,
                pointerEvents: 'auto',
              willChange: 'transform', // Optimize rendering
            }}
          >
            <Tooltip title={expandButtonLabel} arrow>
              <IconButton 
                size="small" 
                onClick={toggleExpanded} 
                sx={STYLES.expandButton}
                aria-expanded={expanded}
                aria-label={expandButtonLabel}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
        
  {createFileDialog}
  {datasourceDialog}
      </Paper>
    </Fade>
  );
});

export default DashboardHeader;
